import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpRequest, HttpHandler, HttpHandlerFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, throwError, from } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

export interface AuthServiceInterface {
  updateAuthToken?(token: string): Observable<any>;
  signOut?(): Observable<any>;
}

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorHelperService {
  private refreshPromise: Promise<string | null> | null = null;
  private updateTokenCalledForCurrentRefresh = false;

  constructor(@Inject(PLATFORM_ID) private readonly platformId: Object, private readonly router: Router, private readonly fireAuth: AngularFireAuth) {}

  /**
   * Handles AUTHORIZATION_EXPIRED error with token refresh for class-based interceptors
   * @param error The HTTP error response
   * @param request The original HTTP request
   * @param next The HTTP handler
   * @param authService Optional auth service for token update
   * @param redirectPath Path to redirect on logout (default: '/')
   * @returns Observable that either retries the request or throws error
   */
  handleAuthorizationExpired(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler,
    authService?: AuthServiceInterface,
    redirectPath: string = '/'
  ): Observable<any> {
    return this.processAuthorizationExpired(error, request, (req) => next.handle(req), authService, redirectPath);
  }

  /**
   * Handles AUTHORIZATION_EXPIRED error with token refresh for functional interceptors
   * @param error The HTTP error response
   * @param request The original HTTP request
   * @param next The HTTP handler function
   * @param authService Optional auth service for token update
   * @param redirectPath Path to redirect on logout (default: '/')
   * @returns Observable that either retries the request or throws error
   */
  handleAuthorizationExpiredFn(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandlerFn,
    authService?: AuthServiceInterface,
    redirectPath: string = '/'
  ): Observable<any> {
    return this.processAuthorizationExpired(error, request, next, authService, redirectPath);
  }

  /**
   * Common logic for handling 401 authentication errors
   */
  private processAuthorizationExpired(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    next: (req: HttpRequest<any>) => Observable<any>,
    authService?: AuthServiceInterface,
    redirectPath: string = '/'
  ): Observable<any> {
    // Handle all 401 Unauthorized errors
    if (error.status === 401) {
      const errorCode = error.error?.code;

      // AUTHORIZATION_EXPIRED means token is expired but can be refreshed
      if (errorCode === 'AUTHORIZATION_EXPIRED' || error.error?.code === 'UNAUTHORIZED_ACCESS') {
        // Try to refresh the Firebase token
        return from(this.refreshFirebaseToken()).pipe(
          switchMap((newToken) => {
            if (newToken) {
              // Update localStorage with new token
              if (isPlatformBrowser(this.platformId)) {
                localStorage.setItem('session', newToken);
              }

              // Update auth token on the server if auth service is provided
              // Only call once per refresh cycle to avoid duplicate calls
              if (authService?.updateAuthToken && !this.updateTokenCalledForCurrentRefresh) {
                this.updateTokenCalledForCurrentRefresh = true;
                authService.updateAuthToken(newToken).subscribe();
              }

              // Retry the original request with the new token
              const retryReq = request.clone({
                headers: request.headers.set('Authorization', newToken),
              });

              return next(retryReq);
            } else {
              // Token refresh failed, logout user
              return this.handleLogout(redirectPath);
            }
          }),
          catchError(() => {
            // Token refresh failed, logout user
            return this.handleLogout(redirectPath);
          })
        );
      }

      // UNAUTHORIZED_ACCESS or any other 401 error means logout immediately
      // (no valid token, invalid token, or token cannot be refreshed)
      return this.handleLogout(redirectPath);
    }

    // Not a 401 error, return as is
    return throwError(() => error);
  }

  /**
   * Attempts to refresh the Firebase ID token with refresh lock mechanism
   * @returns Promise with new token or null if refresh failed
   */
  async refreshFirebaseToken(): Promise<string | null> {
    // Check if a refresh is already in progress
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start new refresh and store the promise
    this.refreshPromise = this.performActualRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      // Clean up the promise and flag regardless of success or failure
      this.refreshPromise = null;
      this.updateTokenCalledForCurrentRefresh = false;
    }
  }

  /**
   * Performs the actual Firebase token refresh logic
   * @returns Promise with new token or null if refresh failed
   */
  private async performActualRefresh(): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      // First check if we have a current user
      let currentUser = await this.fireAuth.currentUser;

      if (!currentUser) {
        // Wait for Firebase auth state to restore (up to 5 seconds)
        currentUser = await this.waitForAuthState();
      }

      if (currentUser) {
        // Force refresh the token
        const newToken = await currentUser.getIdToken(true);
        return newToken;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  /**
   * Waits for Firebase auth state to be restored (useful for page refresh scenarios)
   * @returns Promise with current user or null if timeout
   */
  private async waitForAuthState(): Promise<any> {
    return new Promise(async (resolve) => {
      let unsubscribeFn: (() => void) | null = null;

      // Listen for auth state changes
      const unsubscribePromise = this.fireAuth.onAuthStateChanged((user) => {
        clearTimeout(timeoutId);
        if (unsubscribeFn) {
          unsubscribeFn();
        }
        resolve(user);
      });

      // Wait for the unsubscribe function
      unsubscribeFn = await unsubscribePromise;

      // Set a timeout to avoid waiting forever
      const timeoutId = setTimeout(() => {
        if (unsubscribeFn) {
          unsubscribeFn();
        }
        resolve(null);
      }, 5000);
    });
  }

  /**
   * Handles user logout when token refresh fails
   * @param redirectPath Path to redirect to (default: '/')
   * @returns Observable error
   */
  handleLogout(redirectPath: string = '/'): Observable<never> {
    // Clear session data
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('session');
      localStorage.removeItem('user');
      localStorage.removeItem('preferences');
      localStorage.removeItem('selectedLanguage');
      localStorage.removeItem('language');
    }

    // Determine the correct login path based on the redirect path
    let loginPath = redirectPath;
    if (redirectPath.includes('/auth')) {
      loginPath = '/auth/login';
    }

    // Redirect to login page
    this.router.navigate([loginPath], {
      queryParams: { message: 'Session expired. Please log in again.' },
    });

    return throwError(() => new Error('Session expired and token refresh failed'));
  }

  /**
   * Checks if a request path should skip authentication
   * @param path The request path
   * @param excludedPaths Map of exact paths to exclude
   * @returns boolean indicating if path is excluded
   */
  isExcludedPath(path: string, excludedPaths: Set<string>): boolean {
    return excludedPaths.has(path);
  }
}
